import React, { useState, useEffect } from 'react';
import { entityService } from '../services/entityService.js';
import { useAuth } from '../contexts/AuthContext.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { analyticsEvents } from '../config/analyticsConfig.js';
import toast from 'react-hot-toast';
import LoadingModal from './LoadingModal.js';
import './ScheduleSettings.css';

function ScheduleSettings({ isOpen, onClose, onSave }) {
  usePageTitle('Agenda');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entity, setEntity] = useState(null);
  
  const [scheduleDays, setScheduleDays] = useState([true, true, true, true, true, true, true]);
  const [scheduleStart, setScheduleStart] = useState('08:00');
  const [scheduleEnd, setScheduleEnd] = useState('18:00');
  const [scheduleRange, setScheduleRange] = useState('00:30');
  const [scheduleActive, setScheduleActive] = useState(true);

  const weekDays = [
    { label: 'D', fullName: 'Domingo' },
    { label: 'S', fullName: 'Segunda' },
    { label: 'T', fullName: 'Terça' },
    { label: 'Q', fullName: 'Quarta' },
    { label: 'Q', fullName: 'Quinta' },
    { label: 'S', fullName: 'Sexta' },
    { label: 'S', fullName: 'Sábado' }
  ];

  const formatIntervalLabel = (interval) => {
    if (!interval) return '';
    const [hhStr, mmStr] = interval.split(':');
    const hh = parseInt(hhStr, 10);
    const mm = parseInt(mmStr, 10);
    if (hh === 0) {
      return `${mm} min`;
    }
    return `${hh}h ${String(mm).padStart(2, '0')}`;
  };

  // Verificar se o plano permite agendamentos
  const isPlanValid = entity?.plan?.active && 
    (entity?.plan?.planId === 'alzan.vet.plus' || entity?.plan?.planId === 'alzan.vet.premium');

  useEffect(() => {
    if (isOpen) {
      loadEntityData();
    }
  }, [isOpen]);

  const loadEntityData = async () => {
    try {
      setLoading(true);
      const result = await entityService.getMyEntities();
      
      if (result.success && result.entities && result.entities.length > 0) {
        const entityData = result.entities[0];
        setEntity(entityData);
        
        // Carregar configurações existentes
        if (entityData.scheduleDays) {
          const days = entityData.scheduleDays.split('').map(d => d === '1');
          setScheduleDays(days);
        }
        if (entityData.scheduleStart) setScheduleStart(entityData.scheduleStart);
        if (entityData.scheduleEnd) setScheduleEnd(entityData.scheduleEnd);
        if (entityData.scheduleRange) setScheduleRange(entityData.scheduleRange);
        if (entityData.scheduleActive !== undefined) setScheduleActive(entityData.scheduleActive);
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (index) => {
    const newDays = [...scheduleDays];
    newDays[index] = !newDays[index];
    setScheduleDays(newDays);
  };

  const handleSave = async () => {
    // Validações
    if (!scheduleDays.some(d => d)) {
      toast.error('Selecione pelo menos um dia da semana');
      return;
    }

    if (scheduleStart >= scheduleEnd) {
      toast.error('O horário de início deve ser menor que o horário de término');
      return;
    }

    try {
      setSaving(true);
      
      const scheduleDaysString = scheduleDays.map(d => d ? '1' : '0').join('');
      
      const response = await entityService.updateScheduleSettings(entity.id, {
        scheduleDays: scheduleDaysString,
        scheduleStart,
        scheduleEnd,
        scheduleRange,
        scheduleActive: isPlanValid ? scheduleActive : false
      });

      if (response.success) {
        analyticsEvents.updateScheduleSettings();
        toast.success('Configurações salvas com sucesso!');
        if (onSave) onSave();
        onClose();
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Remove container class from modal root
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="schedule-settings-modal" onClick={(e) => e.stopPropagation()}>
        <LoadingModal isOpen={saving} message="Salvando configurações..." />
        
        <button className="close-button" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {loading ? (
          <div className="schedule-settings-loading">
            <div className="loading-spinner"></div>
            <p>Carregando...</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <header className="schedule-settings-header">
              <div className="header-content">
                <h1 className="header-title">Agendamento</h1>
                <p className="header-subtitle">Defina suas configurações de agendamento.</p>
              </div>
            </header>

            {/* Content */}
            <div className="schedule-settings-content">
              {/* Status do Agendamento */}
              <section className="schedule-settings-section">
                <div className="section-header-toggle">
                  <div>
                    <h3 className="section-title-schedule-settings">Agendamentos Ativos</h3>
                    <p className="schedule-section-description">
                      {isPlanValid 
                        ? 'Permita que clientes façam agendamentos pelo aplicativo'
                        : 'Disponível apenas para planos Plus e Premium'}
                    </p>
                  </div>
                  <button 
                    className={`toggle-switch ${scheduleActive ? 'active' : ''} ${!isPlanValid ? 'disabled' : ''}`}
                    onClick={() => isPlanValid && setScheduleActive(!scheduleActive)}
                    disabled={!isPlanValid}
                    title={!isPlanValid ? 'Faça upgrade para Plus ou Premium' : ''}
                  >
                    <span className="toggle-slider"></span>
                  </button>
                </div>
                {!isPlanValid && (
                  <div className="plan-warning">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span>Para ativar agendamentos, faça upgrade do seu plano</span>
                  </div>
                )}
              </section>

        {/* Dias da Semana */}
        <section className="schedule-settings-section">
          <h3 className="section-title-schedule-settings">Dias da Semana</h3>
          <p className="schedule-section-description">Selecione os dias em que você atende</p>
          <div className="week-days-grid">
            {weekDays.map((day, index) => (
              <button
                key={index}
                className={`day-button ${scheduleDays[index] ? 'active' : ''}`}
                onClick={() => toggleDay(index)}
                title={day.fullName}
              >
                {day.label}
              </button>
            ))}
          </div>
        </section>

        {/* Horário de Atendimento */}
        <section className="schedule-settings-section">
          <h3 className="section-title-schedule-settings">Horário de agendamento</h3>
          <p className="schedule-section-description">Defina o período de atendimento</p>
          <div className="time-range-container">
            <div className="time-input-group">
              <label className="time-label">Início</label>
              <input
                type="time"
                className="time-input"
                value={scheduleStart}
                onChange={(e) => setScheduleStart(e.target.value)}
              />
            </div>
            <div className="time-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </div>
            <div className="time-input-group">
              <label className="time-label">Término</label>
              <input
                type="time"
                className="time-input"
                value={scheduleEnd}
                onChange={(e) => setScheduleEnd(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Intervalo entre Agendamentos */}
        <section className="schedule-settings-section">
          <h3 className="section-title-schedule-settings">Intervalo entre agendamentos</h3>
          <p className="schedule-section-description">Tempo de duração de cada consulta</p>
          <div className="interval-selector">
            {['00:15', '00:30', '00:45', '01:00', '01:30', '02:00'].map((interval) => (
              <button
                key={interval}
                className={`interval-button ${scheduleRange === interval ? 'active' : ''}`}
                onClick={() => setScheduleRange(interval)}
              >
                {formatIntervalLabel(interval)}
              </button>
            ))}
          </div>
        </section>

        {/* Resumo */}
        <section className="schedule-settings-section">
          <h3 className="section-title-schedule-settings">Resumo das Configurações</h3>
          <div className="summary-list">
            <div className="summary-row">
              <span className="summary-label-small">Dias ativos</span>
              <span className="summary-value-small">{scheduleDays.filter(d => d).length} dias</span>
            </div>
            <div className="summary-row">
              <span className="summary-label-small">Horário</span>
              <span className="summary-value-small">{scheduleStart} às {scheduleEnd}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label-small">Intervalo</span>
              <span className="summary-value-small">{formatIntervalLabel(scheduleRange)}</span>
            </div>
          </div>
        </section>
      </div>

      {/* Footer com botão Salvar */}
      <footer className="schedule-settings-footer">
        <button 
          className="save-button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </footer>
    </>
        )}
      </div>
    </div>
  );
}

export default ScheduleSettings;
